<template>
  <chart-line v-bind="chartProps" />
</template>

<script>
import ChartLine from '@/components/chart/AppChartLine.vue'
import moment from 'moment'
import yTitleOnScaleEndPlugin from '@/js/chartjs-plugin/YTitleOnScaleEndPlugin.js'
import customTooltipConfig from '@/components/sensor/CustomTooltipConfig.js'
import getTimeTicksRoundOption from '@/components/sensor/TimeTicksRoundOption.js'
import ObjectUtil from '@/js/util/ObjectUtil'
import {
  TEXT_COLOR,
  ENTITY_COLOR_GREEN,
  ENTITY_COLOR_BLUE,
  ENTITY_COLOR_PURPLE,
  ENTITY_COLOR_LIGHT_GREEN,
  ENTITY_COLOR_LIGHT_BLUE,
  ENTITY_COLOR_RED_PURPLE,
  ENTITY_COLOR_BLUE_GREEN,
  ENTITY_COLOR_PINK,
  ENTITY_COLOR_BROWN,
  ENTITY_COLOR_GOLD,
} from '@/assets/scss/variables.module.scss'

// FonrFamily
const FONT_FAMILY = getComputedStyle(document.body, null).getPropertyValue(
  'font-family'
)

// データセットの色定義
const DATASET_COLORS = [
  ENTITY_COLOR_GREEN,
  ENTITY_COLOR_BLUE,
  ENTITY_COLOR_PURPLE,
  ENTITY_COLOR_LIGHT_GREEN,
  ENTITY_COLOR_LIGHT_BLUE,
  ENTITY_COLOR_RED_PURPLE,
  ENTITY_COLOR_BLUE_GREEN,
  ENTITY_COLOR_PINK,
  ENTITY_COLOR_BROWN,
  ENTITY_COLOR_GOLD,
]

// ユニークキーとする値のキーペア
const UNIQUE_KEY_PAIR = ['sensor_id', 'sensor_item_id']

/**
 * 指定キーでデータをグルーピングする
 * @param {Array.<Object>} data 元データ
 * @returns {Object} グルーピング結果(キーはkeysの配列順にハイフンでjoinしたもの)
 */
const groupBy = (data) => {
  const result = {}

  for (const d of data) {
    const joinedKey = UNIQUE_KEY_PAIR.map((key) => d[key]).join('-')
    if (result[joinedKey] == null) {
      result[joinedKey] = []
    }
    result[joinedKey].push(d)
  }

  return result
}

export default {
  name: 'AppSensorChartLine',
  components: {
    'chart-line': ChartLine,
  },
  props: {
    /**
     * センサーデータ
     */
    data: {
      type: Array,
      default: () => [],
    },
    /**
     * 開始時間
     */
    from: {
      type: [Object, Number],
      default: null,
    },
    /**
     * 終了時間
     */
    to: {
      type: [Object, Number],
      default: null,
    },
    /**
     * 単位
     */
    unit: {
      type: String,
      default: '',
    },
  },
  data: function () {
    return {
      // チャートに渡すprops
      chartProps: {
        data: {},
        options: {},
        plugins: [yTitleOnScaleEndPlugin],
      },
    }
  },
  computed: {
    /**
     * データセット
     * @type {Array.<Object>}
     */
    datasets: function () {
      const grouped = groupBy(this.data)

      const datasets = []
      Object.values(grouped).forEach((data, index) => {
        if (data.length) {
          datasets.push({
            label: this.$_generateLabel(data[0]),
            data,
            borderColor: DATASET_COLORS[index % DATASET_COLORS.length],
          })
        }
      })

      return datasets
    },
    /**
     * チャート上の最大時間
     * @type {Moment}
     */
    maxTime: function () {
      const isAboutToMidnight = this.to.format('HH:mm:ss') === '23:59:59'
      return isAboutToMidnight ? moment(this.to).add(1, 'seconds') : this.to
    },
    /**
     * チャートオプション
     * @type {Object}
     */
    chartOptions: function () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        datasets: {
          line: {
            parsing: {
              xAxisKey: 'sensor_at',
              yAxisKey: 'value',
            },
          },
        },
        elements: {
          point: {
            backgroundColor: '#ffffff00',
            borderWidth: 0,
            hoverBorderWidth: 2,
          },
        },
        scales: {
          x: ObjectUtil.deepMerge(
            {
              type: 'time',
              min: +this.from,
              max: +this.maxTime,
              title: {
                display: true,
                align: 'end',
                text: this.$t('appSensorChartLine.time'),
                font: {
                  size: 10,
                  family: FONT_FAMILY,
                },
                color: '#b5b5b5',
              },
              time: {
                unit: 'minute',
                source: 'data',
                tooltipFormat: 'YYYY/MM/DD HH:mm:ss.SSS',
                round: true,
              },
              ticks: {
                font: {
                  size: 10,
                  family: FONT_FAMILY,
                },
                color: TEXT_COLOR,
                maxRotation: 0,
              },
              grid: {
                color: '#e8e8e8',
                borderColor: '#707070',
                tickWidth: 0,
              },
            },
            getTimeTicksRoundOption(this.from, this.to)
          ),
          y: {
            type: 'linear',
            title: {
              display: false,
            },
            grace: '0.5',
            ticks: {
              font: {
                size: 10,
                family: FONT_FAMILY,
              },
              color: TEXT_COLOR,
            },
            grid: {
              color: '#e8e8e8',
              borderColor: '#707070',
              tickWidth: 0,
              tickLength: 4.5,
            },
          },
        },
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              boxWidth: 14,
              boxHeight: 2,
              padding: 16,
              font: {
                size: 12,
                family: FONT_FAMILY,
              },
              color: TEXT_COLOR,
            },
            onClick: null,
          },
          tooltip: { ...customTooltipConfig },
          yTitleOnScaleEndPlugin: {
            text: this.unit,
            font: {
              size: 10,
              family: FONT_FAMILY,
            },
            color: '#b5b5b5',
          },
        },
      }
    },
  },
  watch: {
    data: function () {
      this.$_updateChartProps()
    },
  },
  methods: {
    /**
     * チャートprops更新
     */
    $_updateChartProps: function () {
      this.chartProps = {
        data: { labels: '', datasets: this.datasets },
        options: this.chartOptions,
        plugins: [yTitleOnScaleEndPlugin],
      }
    },

    /**
     * データセットのラベルを生成する
     * @param {Object} datum センサーデータの1レコード
     * @returns {String} データセットラベル
     */
    $_generateLabel: function (datum) {
      return UNIQUE_KEY_PAIR.map((key) => ({ key, value: datum[key] }))
        .map((item) => {
          switch (item.key) {
            case 'sensor_id':
              return this.$store.getters['sensor/getSensorById'](item.value)
                .sensor_name
            case 'sensor_item_id':
              return this.$store.getters['sensorItem/getSensorItemById'](
                item.value
              ).sensor_item_name
            default:
              return ''
          }
        })
        .join(':')
    },
  },
}
</script>
